import React, { useState, useEffect, useCallback } from 'react';
import BackButton from '../../../ui/BackButton';
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  styled,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
  CircularProgress,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  getOrganizationBranchesPath,
} from '../../../../utils/branchHelper';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  );
});

const Batch = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();
  const canSwitchBranch = isMainBranchAdmin;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddBatchModal, setShowAddBatchModal] = useState(false);

  const [selectedBatch, setSelectedBatch] = useState({
    batchId: null,
    batchName: '',
  });

  // =========================================================
  // FORM DATA
  // =========================================================

  const getInitialFormData = () => ({
    batchCode: '',
    batchName: '',
    startDate: new Date().toISOString().split('T')[0],
    completionDate: new Date(
      Date.now() + 60 * 24 * 60 * 60 * 1000
    )
      .toISOString()
      .split('T')[0],
    batchSize: '',
    courseName: '',
    trainer: '',
    batchStatus: '',
    remark: '',
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // =========================================================
  // DROPDOWN DATA
  // =========================================================

  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);

  // =========================================================
  // GET LOGGED-IN USER
  // =========================================================

  const getSessionUser = () => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (error) {
      console.error('Unable to parse session user:', error);
      return {};
    }
  };

  // =========================================================
  // GET JWT PAYLOAD
  // =========================================================

  const getJwtPayload = () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        return {};
      }

      const tokenParts = accessToken.split('.');

      if (tokenParts.length !== 3) {
        return {};
      }

      return JSON.parse(atob(tokenParts[1]));
    } catch (error) {
      console.error('Unable to decode access token:', error);
      return {};
    }
  };

  // =========================================================
  // STATIC BATCH STATUS
  // =========================================================

  const staticBatchStatusData = [
    { id: 1, name: 'Active' },
    { id: 2, name: 'Completed' },
    { id: 3, name: 'Pending' },
    { id: 4, name: 'to start' },
  ];

  // =========================================================
  // BATCH DATA
  // =========================================================

  const [batchData, setBatchData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

const fetchBatches = useCallback(
  async (branchId = selectedBranchId) => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      const branchParam = branchId ? `?branch=${encodeURIComponent(branchId)}` : '';
      console.log('Fetching batches for branch:', branchId);

      const response = await axios.get(
        `${API_BASE_URL}/api/course/batches/${branchParam}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const responseData = response.data;
      const batchesList = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.results)
        ? responseData.results
        : [];

      console.log('Batches received:', batchesList);

      const transformedData = batchesList.map((batch) => ({
        batchId: batch.id,
        batchCode: batch.batch_code,
        batchName: batch.batch_name,
        startDate: batch.start_date,
        completionDate: batch.completion_date,
        batchSize: batch.batch_size,
        courseId: batch.course,
        courseName: batch.course_name || batch.course_details?.name || `Course ${batch.course}`,
        trainerId: batch.trainer,
        trainer: batch.trainer_name || batch.trainer_details?.name || batch.trainer_details?.username || `Trainer ${batch.trainer}`,
        batchStatus: batch.batch_status,
        remark: batch.remark,
        batchTime: batch.batch_time,
        isActive: batch.is_active,
        originalData: batch,
      }));

      setBatchData(transformedData);
      setFilteredData(transformedData);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setSnackbarMessage(`Failed to load batches: ${error.message}`);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setBatchData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  },
  [navigate, selectedBranchId]
);

  // =========================================================
  // DELETE BATCH
  // =========================================================

  const deleteBatch = async (batchId) => {
    try {
      const accessToken =
        localStorage.getItem('accessToken');

      await axios.delete(
        `${API_BASE_URL}/api/course/batches/${batchId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      await fetchBatches(selectedBranchId);

      setSnackbarMessage(
        `${selectedBatch.batchName} deleted successfully!`
      );
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error(
        'Error deleting batch:',
        error
      );

      setSnackbarMessage(
        `Failed to delete batch: ${error.message}`
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  // =========================================================
  // FETCH BATCHES WHEN BRANCH CHANGES
  // =========================================================

  useEffect(() => {
  fetchBatches(selectedBranchId);
}, [fetchBatches, selectedBranchId]);

  // =========================================================
  // SEARCH
  // =========================================================

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(batchData);
    } else {
      const searchValue =
        searchTerm.toLowerCase();

      const filtered = batchData.filter(
        (batch) =>
          (batch.batchCode &&
            batch.batchCode
              .toLowerCase()
              .includes(searchValue)) ||
          (batch.batchName &&
            batch.batchName
              .toLowerCase()
              .includes(searchValue)) ||
          (batch.courseName &&
            batch.courseName
              .toLowerCase()
              .includes(searchValue)) ||
          (batch.trainer &&
            batch.trainer
              .toLowerCase()
              .includes(searchValue))
      );

      setFilteredData(filtered);
    }

    setPage(1);
  }, [searchTerm, batchData]);

  // =========================================================
  // ADD BATCH
  // =========================================================

  const handleAddBatch = () => {
    navigate('/add-batch');
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (batch) => {
    navigate(`/edit-batch/${batch.batchId}`);
  };

  // =========================================================
  // DELETE MODAL
  // =========================================================

  const handleShowDeleteModal = (
    batchId,
    batchName
  ) => {
    setSelectedBatch({
      batchId,
      batchName,
    });

    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteBatch(selectedBatch.batchId);
    setShowDeleteModal(false);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      formData.batchCode &&
      formData.batchName &&
      formData.startDate &&
      formData.completionDate &&
      formData.batchSize &&
      formData.courseName &&
      formData.trainer &&
      formData.batchStatus
    ) {
      try {
        /*
         * Currently simulated because existing code only
         * contains GET and DELETE API.
         */

        const newBatch = {
          batchId: batchData.length + 1,
          batchCode: formData.batchCode,
          batchName: formData.batchName,
          startDate: formData.startDate,
          completionDate: formData.completionDate,
          batchSize: parseInt(
            formData.batchSize,
            10
          ),
          courseName: formData.courseName,
          trainer: formData.trainer,
          batchStatus: formData.batchStatus,
          remark: formData.remark,
        };

        setBatchData((prev) => [
          ...prev,
          newBatch,
        ]);

        setShowAddBatchModal(false);
        setFormData(getInitialFormData());

        setSnackbarMessage(
          'Batch added successfully!'
        );
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        console.error(
          'Error adding batch:',
          error
        );

        setSnackbarMessage(
          'Failed to add batch. Please try again.'
        );
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } else {
      setSnackbarMessage(
        'Please fill all required fields'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  // =========================================================
  // RESET
  // =========================================================

  const handleCancel = () => {
    setShowAddBatchModal(false);
    setFormData(getInitialFormData());
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(event.target.value, 10)
    );
    setPage(1);
  };

  const paginatedData =
    filteredData.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );

  // =========================================================
  // SAVE TOOLTIP
  // =========================================================

  const getTooltipTitle = () => {
    if (!formData.batchCode)
      return 'Enter Batch Code';

    if (!formData.batchName)
      return 'Enter Batch Name';

    if (!formData.startDate)
      return 'Select Start Date';

    if (!formData.completionDate)
      return 'Select Completion Date';

    if (!formData.batchSize)
      return 'Enter Batch Size';

    if (!formData.courseName)
      return 'Select Course';

    if (!formData.trainer)
      return 'Select Trainer';

    if (!formData.batchStatus)
      return 'Select Batch Status';

    return '';
  };

  const isSaveDisabled =
    !formData.batchCode ||
    !formData.batchName ||
    !formData.startDate ||
    !formData.completionDate ||
    !formData.batchSize ||
    !formData.courseName ||
    !formData.trainer ||
    !formData.batchStatus;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Container>
      <BackButton />

      {/* =====================================================
          TOP BAR
      ====================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            gap: 2,
          }}
        >
          {/* SEARCH */}

          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSearchTerm('')
                    }
                    sx={{
                      color: 'grey',
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),

              sx: {
                borderRadius: '30px',

                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(255, 255, 255, 1)',

                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',

                '& .MuiOutlinedInput-notchedOutline':
                  {
                    border: `1px solid ${
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(0, 0, 0, 0.1)'
                    }`,
                  },

                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(240, 240, 240, 1)',

                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                      : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },

                '&.Mui-focused': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.25)'
                      : 'rgba(255, 255, 255, 1)',

                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,

                  '& .MuiOutlinedInput-notchedOutline':
                    {
                      border: `2px solid ${theme.palette.primary.main}`,
                    },
                },

                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',

              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color:
                  theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          {/* ADD BATCH */}

          <Button
            variant="contained"
            onClick={handleAddBatch}
            sx={{
              ml: 2,
              width: '180px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor:
                theme.palette.primary.main,

              '&:hover': {
                backgroundColor:
                  theme.palette.primary.dark,
              },
            }}
            startIcon={<AddIcon />}
          >
            ADD BATCH
          </Button>
        </Box>

        {/* USER GUIDE */}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() =>
              setShowUserGuideModal(true)
            }
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon
              sx={{ fontSize: 30 }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* =====================================================
          TABLE
      ====================================================== */}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          elevation={3}
          sx={{
            paddingBottom: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            mx: 'auto',
            backgroundColor:
              theme.palette.background.paper,
            borderRadius: '16px',
          }}
        >
          <CustomTableScrollbar
            sx={{
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <TableContainer
              sx={{
                width: '100%',
                borderRadius: '16px',
              }}
            >
              <Table
                sx={{
                  minWidth: 1300,

                  '& .MuiTableHead-root .MuiTableCell-root':
                    {
                      fontWeight: 'bold',
                      backgroundColor:
                        theme.palette.primary.main,
                      color: 'white',
                    },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      S.No
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Batch Code
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Batch Name
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Course Name
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Start Date
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Completion Date
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Trainer
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Strength
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      Status
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor:
                          theme.palette.primary.dark,
                        fontSize: '1.1rem',
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map(
                      (batch, index) => (
                        <TableRow
                          key={batch.batchId}
                          sx={{
                            backgroundColor:
                              theme.palette
                                .background.paper,

                            '&:hover': {
                              backgroundColor:
                                'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {index +
                              1 +
                              (page - 1) *
                                rowsPerPage}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.batchCode || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.batchName || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.courseName || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.startDate || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.completionDate ||
                              '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.trainer || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            {batch.batchSize || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '1rem',
                            }}
                          >
                            <Box
                              sx={{
                                display:
                                  'inline-block',
                                padding:
                                  '4px 12px',
                                borderRadius:
                                  '12px',

                                backgroundColor:
                                  batch.batchStatus ===
                                  'Active'
                                    ? '#e8f5e9'
                                    : batch.batchStatus ===
                                      'Completed'
                                    ? '#e3f2fd'
                                    : batch.batchStatus ===
                                      'to start'
                                    ? '#fff3e0'
                                    : '#f5f5f5',

                                color:
                                  batch.batchStatus ===
                                  'Active'
                                    ? '#2e7d32'
                                    : batch.batchStatus ===
                                      'Completed'
                                    ? '#1565c0'
                                    : batch.batchStatus ===
                                      'to start'
                                    ? '#ef6c00'
                                    : '#757575',

                                fontWeight: '500',
                                fontSize:
                                  '0.875rem',
                              }}
                            >
                              {batch.batchStatus ||
                                '-'}
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <IconButton
                              onClick={() =>
                                handleEdit(batch)
                              }
                              sx={{
                                color:
                                  theme.palette
                                    .primary.light,

                                '&:hover': {
                                  backgroundColor:
                                    'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>

                            <IconButton
                              color="error"
                              onClick={() =>
                                handleShowDeleteModal(
                                  batch.batchId,
                                  batch.batchName
                                )
                              }
                              sx={{
                                '&:hover': {
                                  backgroundColor:
                                    'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  ) : (
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette
                            .background.paper,

                        '&:hover': {
                          backgroundColor:
                            'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{
                          fontSize: '1rem',
                          padding: '20px',
                          wordSpacing: 7,
                        }}
                      >
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTableScrollbar>
        </Paper>
      )}

      {/* =====================================================
          PAGINATION
      ====================================================== */}

      {filteredData.length > 0 &&
        !loading && (
          <Box
            sx={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
              mt: 1,
              p: 2,
              mx: 'auto',
              pt: 2,
            }}
          >
            <Typography
              variant="body2"
              color={
                theme.palette.primary.main
              }
            >
              Page <strong>{page}</strong> of{' '}
              <strong>
                {Math.ceil(
                  filteredData.length /
                    rowsPerPage
                )}
              </strong>
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <IconButton
                onClick={() =>
                  handleChangePage(
                    null,
                    page - 1
                  )
                }
                disabled={page === 1}
                sx={{
                  color:
                    theme.palette.primary.main,

                  '&:hover': {
                    backgroundColor:
                      theme.palette.action
                        .hover,
                  },

                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },

                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                }}
              >
                <ChevronLeft />
              </IconButton>

              {[
                ...Array(
                  Math.ceil(
                    filteredData.length /
                      rowsPerPage
                  )
                ),
              ].map((_, index) => (
                <IconButton
                  key={index + 1}
                  onClick={() =>
                    handleChangePage(
                      null,
                      index + 1
                    )
                  }
                  sx={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',

                    backgroundColor:
                      page === index + 1
                        ? theme.palette.primary
                            .main
                        : 'transparent',

                    color:
                      page === index + 1
                        ? theme.palette.primary
                            .contrastText
                        : theme.palette.text
                            .primary,

                    '&:hover': {
                      backgroundColor:
                        page === index + 1
                          ? theme.palette
                              .primary.dark
                          : theme.palette.action
                              .hover,
                    },
                  }}
                >
                  {index + 1}
                </IconButton>
              ))}

              <IconButton
                onClick={() =>
                  handleChangePage(
                    null,
                    page + 1
                  )
                }
                disabled={
                  page >=
                  Math.ceil(
                    filteredData.length /
                      rowsPerPage
                  )
                }
                sx={{
                  color:
                    theme.palette.primary.main,

                  '&:hover': {
                    backgroundColor:
                      theme.palette.action
                        .hover,
                  },

                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },

                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                }}
              >
                <ChevronRight />
              </IconButton>

              <FormControl
                size="small"
                sx={{
                  minWidth: 100,
                  ml: 1,
                }}
              >
                <InputLabel
                  id="rows-per-page-label"
                  sx={{
                    color:
                      theme.palette.text
                        .primary,
                  }}
                >
                  Rows
                </InputLabel>

                <Select
                  labelId="rows-per-page-label"
                  value={rowsPerPage}
                  onChange={
                    handleChangeRowsPerPage
                  }
                  label="Rows"
                  sx={{
                    height: '36px',
                    color:
                      theme.palette.text
                        .primary,

                    '& .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor:
                          theme.palette.text
                            .primary,
                      },

                    '&:hover .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor:
                          theme.palette
                            .primary.main,
                      },
                  }}
                >
                  {rowsPerPageOptions.map(
                    (option) => (
                      <MenuItem
                        key={option}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        )}

      {/* =====================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* =====================================================
          DELETE MODAL
      ====================================================== */}

      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '12px 16px',
            }}
          >
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{ color: '#fff' }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          <Box sx={{ padding: '20px' }}>
            <Typography
              id="delete-modal-description"
              sx={{ mb: 2 }}
            >
              Are you sure you want to delete{' '}
              <strong>
                {selectedBatch.batchName}
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'flex-end',
                mt: 3,
              }}
            >
              <Button
                onClick={
                  handleCloseDeleteModal
                }
                variant="outlined"
                sx={{ mr: 1 }}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          ADD BATCH MODAL
      ====================================================== */}

      <Modal
        open={showAddBatchModal}
        onClose={() =>
          setShowAddBatchModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              Add Batch
            </Typography>

            <IconButton
              onClick={() =>
                setShowAddBatchModal(false)
              }
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight:
                'calc(80vh - 64px)',
              overflowY: 'auto',
              padding: '20px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection:
                  'column',
                gap: 2,
              }}
            >
              {/* BATCH CODE + NAME */}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <TextField
                  label="Batch Code"
                  variant="outlined"
                  fullWidth
                  name="batchCode"
                  value={
                    formData.batchCode
                  }
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />

                <TextField
                  label="Batch Name"
                  variant="outlined"
                  fullWidth
                  name="batchName"
                  value={
                    formData.batchName
                  }
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />
              </Box>

              {/* DATES */}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <TextField
                  label="Start Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  name="startDate"
                  value={
                    formData.startDate
                  }
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />

                <TextField
                  label="Completion Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  name="completionDate"
                  value={
                    formData.completionDate
                  }
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />
              </Box>

              {/* SIZE + COURSE */}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <TextField
                  label="Batch Size"
                  variant="outlined"
                  fullWidth
                  name="batchSize"
                  value={
                    formData.batchSize
                  }
                  onChange={handleChange}
                  type="number"
                  required
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />

                <FormControl
                  fullWidth
                  required
                >
                  <InputLabel
                    id="courseName-label"
                    sx={{
                      color:
                        theme.palette.text
                          .primary,

                      '&.Mui-focused': {
                        color:
                          theme.palette
                            .primary.main,
                      },
                    }}
                  >
                    Course
                  </InputLabel>

                  <Select
                    labelId="courseName-label"
                    name="courseName"
                    value={
                      formData.courseName
                    }
                    label="Course"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette.text
                              .primary,
                        },

                      '&:hover .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette
                              .primary.main,
                        },
                    }}
                  >
                    {courses.length > 0 ? (
                      courses.map(
                        (course) => (
                          <MenuItem
                            key={course.id}
                            value={course.id}
                          >
                            {course.course_name ||
                              course.name}
                          </MenuItem>
                        )
                      )
                    ) : (
                      <MenuItem
                        value=""
                        disabled
                      >
                        Loading courses...
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* TRAINER + STATUS */}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <FormControl
                  fullWidth
                  required
                >
                  <InputLabel
                    id="trainer-label"
                    sx={{
                      color:
                        theme.palette.text
                          .primary,

                      '&.Mui-focused': {
                        color:
                          theme.palette
                            .primary.main,
                      },
                    }}
                  >
                    Trainer
                  </InputLabel>

                  <Select
                    labelId="trainer-label"
                    name="trainer"
                    value={
                      formData.trainer
                    }
                    label="Trainer"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette.text
                              .primary,
                        },

                      '&:hover .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette
                              .primary.main,
                        },
                    }}
                  >
                    {trainers.length > 0 ? (
                      trainers.map(
                        (trainer) => (
                          <MenuItem
                            key={trainer.id}
                            value={trainer.id}
                          >
                            {trainer.name ||
                              trainer.username}
                          </MenuItem>
                        )
                      )
                    ) : (
                      <MenuItem
                        value=""
                        disabled
                      >
                        Loading trainers...
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  required
                >
                  <InputLabel
                    id="batchStatus-label"
                    sx={{
                      color:
                        theme.palette.text
                          .primary,

                      '&.Mui-focused': {
                        color:
                          theme.palette
                            .primary.main,
                      },
                    }}
                  >
                    Batch Status
                  </InputLabel>

                  <Select
                    labelId="batchStatus-label"
                    name="batchStatus"
                    value={
                      formData.batchStatus
                    }
                    label="Batch Status"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette.text
                              .primary,
                        },

                      '&:hover .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor:
                            theme.palette
                              .primary.main,
                        },
                    }}
                  >
                    {staticBatchStatusData.map(
                      (status) => (
                        <MenuItem
                          key={status.id}
                          value={status.name}
                        >
                          {status.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* REMARK */}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <TextField
                  label="Remark"
                  variant="outlined"
                  fullWidth
                  name="remark"
                  value={
                    formData.remark
                  }
                  onChange={handleChange}
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiInputLabel-root':
                      {
                        color:
                          theme.palette.text
                            .primary,
                      },

                    '& .MuiInputLabel-root.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary.main,
                      },
                  }}
                />
              </Box>

              {/* BUTTONS */}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent:
                    'flex-end',
                  mt: 2,
                }}
              >
                <Tooltip
                  title={getTooltipTitle()}
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        mr: 2,
                        backgroundColor:
                          theme.palette
                            .primary.main,

                        '&:hover': {
                          backgroundColor:
                            theme.palette
                              .primary.dark,
                        },
                      }}
                      disabled={
                        isSaveDisabled
                      }
                    >
                      SAVE
                    </Button>
                  </span>
                </Tooltip>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                >
                  RESET
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          USER GUIDE MODAL
      ====================================================== */}

      <Modal
        open={showUserGuideModal}
        onClose={() =>
          setShowUserGuideModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              Batch Module - User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(false)
              }
              sx={{
                color: '#fff',
                position: 'absolute',
                right: 14,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* CONTENT */}

          <CustomTableScrollbar
            sx={{
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '20px',
            }}
          >
            {/* SECTION 1 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Follow these steps to add a
              new batch:
            </Typography>

            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Click the{' '}
                <strong>
                  ADD BATCH
                </strong>{' '}
                button at the top right to
                open the add batch form.
              </li>

              <li>
                Fill in all required fields:
                Batch Code, Batch Name, Start
                Date, Completion Date, Batch
                Size, Course, Trainer, and
                Batch Status.
              </li>

              <li>
                Select a batch status (e.g.,
                Active, Completed, Pending)
                from the dropdown.
              </li>

              <li>
                Optionally, add a remark in
                the provided text area.
              </li>

              <li>
                Review all entered information
                carefully before submission.
              </li>

              <li>
                Click the{' '}
                <strong>SAVE</strong> button to
                add the batch. A success
                message will confirm the
                addition.
              </li>

              <li>
                The new batch record will appear
                in the Batch List table.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: All fields except
              Remark are required. Ensure the
              batch code is unique and the
              batch size is a valid number.
            </Typography>

            {/* SECTION 2 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to update
              an existing batch:
            </Typography>

            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Select the batch you want to
                edit from the Batch List table.
              </li>

              <li>
                Click the{' '}
                <strong>Edit</strong> (pencil
                icon) in the Action column to
                open the edit batch form.
              </li>

              <li>
                Ensure all required fields are
                filled.
              </li>

              <li>
                Click the{' '}
                <strong>UPDATE</strong> button to
                save changes. A success message
                will confirm the update.
              </li>

              <li>
                To discard changes, click the{' '}
                <strong>CANCEL</strong> button.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: All fields except
              Remark are required to avoid
              submission errors.
            </Typography>

            {/* SECTION 3 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to delete
              a batch:
            </Typography>

            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Select the batch you want to
                delete from the Batch List table.
              </li>

              <li>
                Click the{' '}
                <strong>Delete</strong> (trash
                icon) in the Action column.
              </li>

              <li>
                Confirm the deletion in the
                modal by clicking{' '}
                <strong>
                  Yes, Delete
                </strong>
                .
              </li>

              <li>
                The batch will be removed, and
                a success message will appear.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: Deleted batches cannot be
              recovered. Proceed with caution.
            </Typography>

            {/* SECTION 4 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to search
              for a batch:
            </Typography>

            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the search bar at the top
                left of the Batch List.
              </li>

              <li>
                Enter the batch code, batch name,
                course name, or trainer to filter
                the table.
              </li>

              <li>
                The table will update
                automatically to show matching
                batches.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: The search is
              case-insensitive and filters by
              Batch Code, Batch Name, Course Name,
              or Trainer.
            </Typography>

            {/* SECTION 5 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to navigate
              the batch list:
            </Typography>

            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the pagination controls at
                the bottom to browse pages.
              </li>

              <li>
                Click the left (
                <strong>&lt;</strong>) or right (
                <strong>&gt;</strong>) arrows to
                switch pages.
              </li>

              <li>
                Select a specific page number by
                clicking on it.
              </li>

              <li>
                Adjust the number of rows per
                page using the dropdown menu
                (e.g., 5, 10, 25, 50 rows).
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: The table displays only
              the selected number of rows per
              page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Batch;